import React, { createRef } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Input } from '../../../components';
import { classNames, Constants, delay, fromStorage } from '../../../utils';
import { PurchasesService } from '../../../services';

const getStatusColor = (status) => {
  switch (status) {
    case Constants.ORDER.STATUS.PENDING:
      return { bg: 'bg-orange', fg: 'text-white' };

    case Constants.ORDER.STATUS.APPROVED:
      return { bg: 'bg-green', fg: 'text-white' };

    case Constants.ORDER.STATUS.REJECTED:
    default:
      return { bg: 'bg-[#DDDDDD]', fg: 'text-black' };
  }
};

const INITIAL_FILTERS = {
  since: '',
  until: '',
  page: 1,
  pages: 1,
};

class RequestHistory extends React.Component {

  state = {
    isLoading: false,
    form: { ...INITIAL_FILTERS },
    requests: [],
  }

  goBack = createRef();

  componentDidMount() {
    this.goBack.current = document.getElementById('header-goback');
    this.goBack.current.classList.remove('hidden');

    this.getRequests();
  }

  componentWillUnmount() {
    this.goBack.current.classList.add('hidden');
  }

  getRequests = async (page = 1) => {
    const { form } = this.state;

    try {
      const data = {
        ...form,
        since: form.since ? moment(form.since).format('YYYY-MM-DD') : '',
        until: form.until ? moment(form.until).format('YYYY-MM-DD') : '',
      };

      const res = await PurchasesService.getServicesHistory(data);
      this.setState({
        requests: res.data,
        form: { ...form, page },
      });

    } catch (error) {
      console.log(error);
      toast.error('Ocurrió un error inesperado');
    }
  }

  change = async (value,target) => {
    await this.setState({
      form: {
        ...this.state.form,
        [target]: value,
      },
    });

    this.getRequests();
  }

  canCleanFilters = () => {
    const current = this.state.form;
    const initial = INITIAL_FILTERS;
    return current.since !== initial.since || current.until !== initial.until;
  }

  cleanFilters = async () => {
    await this.setState({ form: { ...INITIAL_FILTERS } });
    await delay(1);
    this.getRequests();
  }

  confirmReceived = async (item) => {
    this.setState({ isLoading: true });

    try {
      await PurchasesService.confirmReceived({ id: item.id });
      this.getRequests();

    } catch (error) {
      console.log(error);
    }

    this.setState({ isLoading: false });
  }

  downloadPrintableOrder = (item) => {
    const url = item.documents?.find(x => x.file_id === Constants.ORDER_DOC.FILENAME.ORDER)?.url;
    const link = document.createElement('a');
    link.href = fromStorage(url);
    link.download = 'Orden';
    link.target = '_blank';
    link.click();
    link.remove();
  }

  render() {

    const { form, requests } = this.state;
    const canCleanFilters = this.canCleanFilters();

    return (
      <>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-4 mb-8 mt-8 lg:mt-0">
          <div className="w-full sm:w-40">
            <Input
              type="date"
              placeholder="Desde"
              value={form.since}
              onValueChange={v => this.change(v,'since')}
            />
          </div>
          <div className="w-full sm:w-40">
            <Input
              type="date"
              placeholder="Hasta"
              value={form.until}
              onValueChange={v => this.change(v,'until')}
            />
          </div>
          {canCleanFilters && (
            <button onClick={this.cleanFilters} className="text-left">
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        <div className="max-w-[600px] mx-auto flex flex-col gap-4">
          {requests.map((item, index) => (
            <RequestItem
              key={index}
              item={item}
              confirmReceived={this.confirmReceived}
              downloadPrintableOrder={() => this.downloadPrintableOrder(item)}
            />
          ))}

          {form.page < form.pages && (
            <div className="flex justify-center mt-2">
              <button
                className="ml-0 bg-green px-4 py-1 rounded-full font-semibold text-white"
                onClick={() => this.getRequests(form.page + 1)}
              >
                <span>Cargar más</span>
              </button>
            </div>
          )}

          {!requests.length && (
            <p className="font-semibold text-gray3 text-center my-10">
              No hay pagos
            </p>
          )}
        </div>
      </>
    );
  }
}

const RequestItem = ({ item, confirmReceived, downloadPrintableOrder }) => {
  const number = String(item.id).padStart(4,'0');
  const date = moment(item.created_at).format('DD-MM-YYYY');
  const status = getStatusColor(item.status);
  const isPending = item.status === Constants.ORDER.STATUS.PENDING;
  const isRejected = item.status === Constants.ORDER.STATUS.REJECTED;
  const printableOrder = item.documents?.find(x => x.file_id === Constants.ORDER_DOC.FILENAME.ORDER);

  return (
    <div className="bg-white rounded-lg border border-gray6 p-2">
      <div className="flex justify-between items-center">
        <p className="font-bold text-darkBlue flex-1">
          N° { number } / { date }
        </p>
        <div className={classNames('px-2 py-1 rounded-full text-sm', status.bg)}>
          <span className={classNames('font-semibold', status.fg)}>
            { item.status_text }
          </span>
        </div>
      </div>
      <p>
        <span className="font-semibold">Tipo de Servicio: </span>
        <span>{ item.purchase_service?.service?.name }</span>
      </p>
      <p>
        <span className="font-semibold">Beneficiario: </span>
        <span>{ item.user?.fullName }</span>
      </p>
      {!!item?.show_auth_code && (
        <p>
          <span className="font-semibold">Autorización: </span>
          <span>{ item.auth_code }</span>
        </p>
      )}
      {isPending && (
        <p className="text-red text-xs font-semibold">SU SOLICITUD ESTA EN PROCESO DE ANALISIS POR NUESTRO EJECUTIVO</p>
      )}
      {isRejected && (
        <p className="text-red text-xs font-semibold">EL EJECUTIVO LO ESTARA CONTACTANDO PARA DARLE MAYOR INFORMACION</p>
      )}

      <div className="flex justify-between items-center mt-1">
        <div>
          {item.status === Constants.ORDER.STATUS.APPROVED && (
            <button
              className="flex items-center"
              onClick={() => !item.confirm_received ? confirmReceived(item) : {}}
              disabled={!!item.confirm_received}
            >
              <div
                className={classNames(
                  'w-4 h-4 rounded-[4px] border mr-2',
                  item.confirm_received ? 'bg-green border-green' : 'bg-[#DDDDDD] border-[#999999]'
                )}
              />
              <span>{ !item.confirm_received ? 'Confirmar' : 'Confirmado' }</span>
            </button>
          )}
        </div>

        <div>
          {!!printableOrder && (
            <button onClick={downloadPrintableOrder}>
              <p className="text-blue font-semibold underline">Descargar orden</p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestHistory;
